import apiClient, { ApiResponse, PaginatedResponse } from '../api/client';
import { County, User, Module } from '../types/models';

// Super Admin Dashboard Stats
export interface SuperAdminDashboardStats {
  total_counties: number;
  total_users: number;
  total_revenue: number;
  total_transactions: number;
  recent_transactions?: any[];
}

// County Management
export interface CreateCountyData {
  county_id: number;
  /** Portal subdomain (e.g. "nakuru"); server auto-slugs the county name when omitted. */
  subdomain?: string;
  office_location?: string;
  website?: string;
  logo?: File;
  contacts?: Record<number, string>;
  module_types: number[];
  touchpoints?: Array<'portal' | 'mobile' | 'whatsapp'>;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  billing?: Record<string, unknown>;
}

export interface UpdateCountyData extends Partial<CreateCountyData> {}

export interface CountyStats {
  total_modules: number;
  total_users: number;
  total_revenue: number;
  total_transactions: number;
  total_customers: number;
}

class SuperAdminService {
  private baseUrl = '/admin/superadmin';

  // Dashboard
  async getDashboardStats(params?: { date_filter?: string; start_date?: string; end_date?: string }): Promise<ApiResponse<SuperAdminDashboardStats>> {
    const queryParams = new URLSearchParams();
    if (params?.date_filter) queryParams.append('date_filter', params.date_filter);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    const queryString = queryParams.toString();
    return apiClient.get<SuperAdminDashboardStats>(`${this.baseUrl}/dashboard/stats${queryString ? `?${queryString}` : ''}`);
  }

  async getRevenueChart(params?: { monthly_filter?: string; start_date?: string; end_date?: string }): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams();
    if (params?.monthly_filter) queryParams.append('monthly_filter', params.monthly_filter);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    const queryString = queryParams.toString();
    return apiClient.get(`${this.baseUrl}/dashboard/revenue-chart${queryString ? `?${queryString}` : ''}`);
  }

  async getCountyPerformance(params?: { date_filter?: string; start_date?: string; end_date?: string }): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams();
    if (params?.date_filter) queryParams.append('date_filter', params.date_filter);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    const queryString = queryParams.toString();
    return apiClient.get(`${this.baseUrl}/dashboard/county-performance${queryString ? `?${queryString}` : ''}`);
  }

  async getRecentTransactions(params?: { limit?: number }): Promise<ApiResponse<any[]>> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    const queryString = queryParams.toString();
    return apiClient.get<any[]>(`${this.baseUrl}/dashboard/recent-transactions${queryString ? `?${queryString}` : ''}`);
  }

  // County Management
  async getCounties(page?: number, perPage?: number): Promise<ApiResponse<PaginatedResponse<County>>> {
    const qs = new URLSearchParams();
    if (page) qs.append('page', String(page));
    if (perPage) qs.append('per_page', String(perPage));
    const s = qs.toString();
    return apiClient.get<PaginatedResponse<County>>(`${this.baseUrl}/counties${s ? `?${s}` : ''}`);
  }

  async getCounty(id: number): Promise<ApiResponse<County>> {
    return apiClient.get<County>(`${this.baseUrl}/counties/${id}`);
  }

  async createCounty(data: CreateCountyData): Promise<ApiResponse<County>> {
    const formData = new FormData();

    // Add simple fields
    formData.append('county_id', String(data.county_id));
    formData.append('first_name', data.first_name);
    formData.append('last_name', data.last_name);
    formData.append('email', data.email);
    formData.append('phone', data.phone);

    if (data.subdomain) formData.append('subdomain', data.subdomain);
    if (data.office_location) formData.append('office_location', data.office_location);
    if (data.website) formData.append('website', data.website);
    if (data.logo) formData.append('logo', data.logo);

    // Add contacts as array
    if (data.contacts) {
      Object.entries(data.contacts).forEach(([contactId, value]) => {
        if (value) formData.append(`contacts[${contactId}]`, value);
      });
    }

    // Add module_types as array
    data.module_types.forEach(moduleTypeId => {
      formData.append('module_types[]', String(moduleTypeId));
    });

    // Add touchpoints as array (channels to enable for this county)
    if (data.touchpoints) {
      data.touchpoints.forEach((channel) => {
        formData.append('touchpoints[]', channel);
      });
    }

    // Add optional billing payload as flat dot-notation fields so Laravel parses it as an array
    if (data.billing) {
      const flatten = (obj: any, prefix: string) => {
        Object.entries(obj).forEach(([k, v]) => {
          if (v === null || v === undefined) return;
          const key = `${prefix}[${k}]`;
          if (Array.isArray(v)) {
            v.forEach((item, idx) => {
              if (item !== null && typeof item === 'object') {
                flatten(item, `${key}[${idx}]`);
              } else {
                formData.append(`${key}[${idx}]`, String(item));
              }
            });
          } else if (typeof v === 'object') {
            flatten(v, key);
          } else {
            formData.append(key, String(v));
          }
        });
      };
      flatten(data.billing, 'billing');
    }

    return apiClient.upload<County>(`${this.baseUrl}/counties`, formData);
  }

  async updateCounty(id: number, data: UpdateCountyData): Promise<ApiResponse<County>> {
    if (data.logo) {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (value instanceof File) {
            formData.append(key, value);
          } else {
            formData.append(key, String(value));
          }
        }
      });
      formData.append('_method', 'PUT');
      return apiClient.upload<County>(`${this.baseUrl}/counties/${id}`, formData);
    }
    return apiClient.put<County>(`${this.baseUrl}/counties/${id}`, data);
  }

  async deleteCounty(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.baseUrl}/counties/${id}`);
  }

  /**
   * Touchpoints (by-county view: returns the three supported channels with an `exists` flag).
   */
  async getCountyTouchpoints(countyId: number): Promise<ApiResponse<Array<{
    channel: 'portal' | 'mobile' | 'whatsapp';
    exists: boolean;
    touchpoint: { id: number; username: string; status: 'active' | 'inactive'; touch_point: string } | null;
  }>>> {
    return apiClient.get(`${this.baseUrl}/counties/${countyId}/touchpoints`);
  }

  async upsertCountyTouchpoint(
    countyId: number,
    channel: 'portal' | 'mobile' | 'whatsapp',
    payload: { status?: 'active' | 'inactive'; regenerate?: boolean } = {},
  ): Promise<ApiResponse<any> & { credentials?: { username: string; key: string } }> {
    return apiClient.post(`${this.baseUrl}/counties/${countyId}/touchpoints/${channel}`, payload) as any;
  }

  async getCountyStats(id: number): Promise<ApiResponse<CountyStats>> {
    return apiClient.get<CountyStats>(`${this.baseUrl}/counties/${id}/stats`);
  }

  async getCountyUsers(countyId: number): Promise<ApiResponse<User[]>> {
    return apiClient.get<User[]>(`${this.baseUrl}/counties/${countyId}/users`);
  }

  async getCountyModules(countyId: number): Promise<ApiResponse<Module[]>> {
    return apiClient.get<Module[]>(`${this.baseUrl}/counties/${countyId}/modules`);
  }

  async suspendCountyUser(countyId: number, userId: number): Promise<ApiResponse<User>> {
    return apiClient.post<User>(`${this.baseUrl}/counties/${countyId}/users/${userId}/suspend`, {});
  }

  async unsuspendCountyUser(countyId: number, userId: number): Promise<ApiResponse<User>> {
    return apiClient.post<User>(`${this.baseUrl}/counties/${countyId}/users/${userId}/unsuspend`, {});
  }

  // County User Management
  async getCountyUsersList(page?: number): Promise<ApiResponse<PaginatedResponse<User>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<User>>(`${this.baseUrl}/county-users${params}`);
  }

  async getCountyUser(id: number): Promise<ApiResponse<User>> {
    return apiClient.get<User>(`${this.baseUrl}/county-users/${id}`);
  }

  async createCountyUser(data: any): Promise<ApiResponse<User>> {
    return apiClient.post<User>(`${this.baseUrl}/county-users`, data);
  }

  async updateCountyUser(id: number, data: any): Promise<ApiResponse<User>> {
    return apiClient.put<User>(`${this.baseUrl}/county-users/${id}`, data);
  }

  async deleteCountyUser(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.baseUrl}/county-users/${id}`);
  }

  // County Roles & Permissions
  async getCountyRoles(): Promise<ApiResponse<any[]>> {
    return apiClient.get<any[]>(`${this.baseUrl}/county-roles`);
  }

  async getCountyPermissions(): Promise<ApiResponse<any[]>> {
    return apiClient.get<any[]>(`${this.baseUrl}/county-permissions`);
  }

  // Touch Points Management
  async getTouchPoints(page?: number, perPage?: number): Promise<ApiResponse<PaginatedResponse<any>>> {
    const qs = new URLSearchParams();
    if (page) qs.append('page', String(page));
    if (perPage) qs.append('per_page', String(perPage));
    const s = qs.toString();
    return apiClient.get<PaginatedResponse<any>>(`${this.baseUrl}/touch-points${s ? `?${s}` : ''}`);
  }

  async getTouchPoint(id: number): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`${this.baseUrl}/touch-points/${id}`);
  }

  async createTouchPoint(data: any): Promise<ApiResponse<any>> {
    return apiClient.post<any>(`${this.baseUrl}/touch-points`, data);
  }

  async updateTouchPoint(id: number, data: any): Promise<ApiResponse<any>> {
    return apiClient.put<any>(`${this.baseUrl}/touch-points/${id}`, data);
  }

  async deleteTouchPoint(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.baseUrl}/touch-points/${id}`);
  }

  async regenerateTouchPointKey(id: number): Promise<ApiResponse<any>> {
    return apiClient.post<any>(`${this.baseUrl}/touch-points/${id}/regenerate-key`);
  }

  // Superadmin Users Management
  async getSuperadminUsers(page?: number): Promise<ApiResponse<PaginatedResponse<any>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<any>>(`${this.baseUrl}/superadmin-users${params}`);
  }

  async getSuperadminUser(id: number): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`${this.baseUrl}/superadmin-users/${id}`);
  }

  async createSuperadminUser(data: any): Promise<ApiResponse<any>> {
    return apiClient.post<any>(`${this.baseUrl}/superadmin-users`, data);
  }

  async updateSuperadminUser(id: number, data: any): Promise<ApiResponse<any>> {
    return apiClient.put<any>(`${this.baseUrl}/superadmin-users/${id}`, data);
  }

  async deleteSuperadminUser(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.baseUrl}/superadmin-users/${id}`);
  }

  async toggleSuperadminUserStatus(id: number): Promise<ApiResponse<any>> {
    return apiClient.post<any>(`${this.baseUrl}/users/${id}/toggle-status`, {});
  }

  // Roles & Permissions (Superadmin)
  async getSuperadminRoles(): Promise<ApiResponse<any[]>> {
    return apiClient.get<any[]>(`${this.baseUrl}/roles?all=true`);
  }

  async getSuperadminRole(id: number): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`${this.baseUrl}/roles/${id}`);
  }

  async createSuperadminRole(data: { name: string; guard_name?: string; permissions?: number[] }): Promise<ApiResponse<any>> {
    return apiClient.post<any>(`${this.baseUrl}/roles`, data);
  }

  async updateSuperadminRole(id: number, data: { name?: string; guard_name?: string; permissions?: number[] }): Promise<ApiResponse<any>> {
    return apiClient.put<any>(`${this.baseUrl}/roles/${id}`, data);
  }

  async deleteSuperadminRole(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.baseUrl}/roles/${id}`);
  }

  async getSuperadminPermissions(): Promise<ApiResponse<any[]>> {
    return apiClient.get<any[]>(`${this.baseUrl}/permissions?all=true`);
  }

  async getSuperadminPermission(id: number): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`${this.baseUrl}/permissions/${id}`);
  }

  async createSuperadminPermission(data: { name: string; guard_name?: string }): Promise<ApiResponse<any>> {
    return apiClient.post<any>(`${this.baseUrl}/permissions`, data);
  }

  async updateSuperadminPermission(id: number, data: { name?: string; guard_name?: string }): Promise<ApiResponse<any>> {
    return apiClient.put<any>(`${this.baseUrl}/permissions/${id}`, data);
  }

  async deleteSuperadminPermission(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.baseUrl}/permissions/${id}`);
  }

  // Payment Modules Management
  async getPaymentModules(page?: number): Promise<ApiResponse<PaginatedResponse<any>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<any>>(`${this.baseUrl}/payment-modules${params}`);
  }

  async getPaymentModule(id: number): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`${this.baseUrl}/payment-modules/${id}`);
  }

  async createPaymentModule(data: {
    name: string;
    provider: string;
    description?: string;
    status?: string;
    fields?: Array<{
      name: string;
      type: string;
      options?: string;
      required?: boolean;
    }>;
  }): Promise<ApiResponse<any>> {
    return apiClient.post<any>(`${this.baseUrl}/payment-modules`, data);
  }

  async updatePaymentModule(id: number, data: {
    name?: string;
    provider?: string;
    description?: string;
    status?: string;
    fields?: Array<{
      name: string;
      type: string;
      options?: string;
      required?: boolean;
    }>;
  }): Promise<ApiResponse<any>> {
    return apiClient.put<any>(`${this.baseUrl}/payment-modules/${id}`, data);
  }

  async deletePaymentModule(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.baseUrl}/payment-modules/${id}`);
  }

  // Transactions Management
  async getTransactions(page?: number, params?: any): Promise<ApiResponse<PaginatedResponse<any>>> {
    const queryParams = new URLSearchParams();
    if (page) queryParams.append('page', String(page));
    if (params?.status) queryParams.append('status', params.status);
    if (params?.county_id) queryParams.append('county_id', params.county_id);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    const queryString = queryParams.toString();
    return apiClient.get<PaginatedResponse<any>>(`${this.baseUrl}/transactions${queryString ? `?${queryString}` : ''}`);
  }

  async getTransaction(id: number): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`${this.baseUrl}/transactions/${id}`);
  }

  async getTransactionStats(): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`${this.baseUrl}/transactions/stats`);
  }

  async getSuccessfulTransactions(page?: number, params?: any): Promise<ApiResponse<PaginatedResponse<any>>> {
    const queryParams = new URLSearchParams();
    if (page) queryParams.append('page', String(page));
    if (params?.county_id) queryParams.append('county_id', params.county_id);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    const queryString = queryParams.toString();
    return apiClient.get<PaginatedResponse<any>>(`${this.baseUrl}/transactions/successful${queryString ? `?${queryString}` : ''}`);
  }

  async getFailedTransactions(page?: number, params?: any): Promise<ApiResponse<PaginatedResponse<any>>> {
    const queryParams = new URLSearchParams();
    if (page) queryParams.append('page', String(page));
    if (params?.county_id) queryParams.append('county_id', params.county_id);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    const queryString = queryParams.toString();
    return apiClient.get<PaginatedResponse<any>>(`${this.baseUrl}/transactions/failed${queryString ? `?${queryString}` : ''}`);
  }

  // Modules Management
  async getModules(page?: number): Promise<ApiResponse<PaginatedResponse<any>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<any>>(`${this.baseUrl}/modules${params}`);
  }

  async getModule(id: number): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`${this.baseUrl}/modules/${id}`);
  }

  async createModule(data: any): Promise<ApiResponse<any>> {
    return apiClient.post<any>(`${this.baseUrl}/modules`, data);
  }

  async updateModule(id: number, data: any): Promise<ApiResponse<any>> {
    return apiClient.put<any>(`${this.baseUrl}/modules/${id}`, data);
  }

  async deleteModule(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.baseUrl}/modules/${id}`);
  }

  // Schedules Management
  async getSchedules(page?: number): Promise<ApiResponse<PaginatedResponse<any>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<any>>(`${this.baseUrl}/schedules${params}`);
  }

  async getSchedule(id: number): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`${this.baseUrl}/schedules/${id}`);
  }

  async createSchedule(data: any): Promise<ApiResponse<any>> {
    return apiClient.post<any>(`${this.baseUrl}/schedules`, data);
  }

  async updateSchedule(id: number, data: any): Promise<ApiResponse<any>> {
    return apiClient.put<any>(`${this.baseUrl}/schedules/${id}`, data);
  }

  async deleteSchedule(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.baseUrl}/schedules/${id}`);
  }

  // Parts Management
  async getParts(page?: number): Promise<ApiResponse<PaginatedResponse<any>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<any>>(`${this.baseUrl}/parts${params}`);
  }

  async getPart(id: number): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`${this.baseUrl}/parts/${id}`);
  }

  async createPart(data: any): Promise<ApiResponse<any>> {
    return apiClient.post<any>(`${this.baseUrl}/parts`, data);
  }

  async updatePart(id: number, data: any): Promise<ApiResponse<any>> {
    return apiClient.put<any>(`${this.baseUrl}/parts/${id}`, data);
  }

  async deletePart(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.baseUrl}/parts/${id}`);
  }

  // Categories Management
  async getCategories(page?: number): Promise<ApiResponse<PaginatedResponse<any>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<any>>(`${this.baseUrl}/categories${params}`);
  }

  async getCategory(id: number): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`${this.baseUrl}/categories/${id}`);
  }

  async createCategory(data: any): Promise<ApiResponse<any>> {
    return apiClient.post<any>(`${this.baseUrl}/categories`, data);
  }

  async updateCategory(id: number, data: any): Promise<ApiResponse<any>> {
    return apiClient.put<any>(`${this.baseUrl}/categories/${id}`, data);
  }

  async deleteCategory(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.baseUrl}/categories/${id}`);
  }

  // Services Management
  async getServices(page?: number): Promise<ApiResponse<PaginatedResponse<any>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<any>>(`${this.baseUrl}/services${params}`);
  }

  async getService(id: number): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`${this.baseUrl}/services/${id}`);
  }

  async createService(data: any): Promise<ApiResponse<any>> {
    return apiClient.post<any>(`${this.baseUrl}/services`, data);
  }

  async updateService(id: number, data: any): Promise<ApiResponse<any>> {
    return apiClient.put<any>(`${this.baseUrl}/services/${id}`, data);
  }

  async deleteService(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.baseUrl}/services/${id}`);
  }

  // Module Types Management
  async getModuleTypes(page?: number): Promise<ApiResponse<PaginatedResponse<any>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<any>>(`${this.baseUrl}/module-types${params}`);
  }

  async getModuleType(id: number): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`${this.baseUrl}/module-types/${id}`);
  }

  async createModuleType(data: any): Promise<ApiResponse<any>> {
    return apiClient.post<any>(`${this.baseUrl}/module-types`, data);
  }

  async updateModuleType(id: number, data: any): Promise<ApiResponse<any>> {
    return apiClient.put<any>(`${this.baseUrl}/module-types/${id}`, data);
  }

  async deleteModuleType(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.baseUrl}/module-types/${id}`);
  }

  // Notifications Management
  async getNotifications(page?: number): Promise<ApiResponse<PaginatedResponse<any>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<any>>(`${this.baseUrl}/notifications${params}`);
  }

  async getNotification(id: number): Promise<ApiResponse<any>> {
    return apiClient.get<any>(`${this.baseUrl}/notifications/${id}`);
  }

  async createNotification(data: any): Promise<ApiResponse<any>> {
    return apiClient.post<any>(`${this.baseUrl}/notifications`, data);
  }

  async updateNotification(id: number, data: any): Promise<ApiResponse<any>> {
    return apiClient.put<any>(`${this.baseUrl}/notifications/${id}`, data);
  }

  async deleteNotification(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.baseUrl}/notifications/${id}`);
  }

  async sendNotification(id: number): Promise<ApiResponse<any>> {
    return apiClient.post<any>(`${this.baseUrl}/notifications/${id}/send`);
  }

  // Change Password
  async changePassword(data: { current_password: string; new_password: string; new_password_confirmation: string }): Promise<ApiResponse> {
    return apiClient.post(`${this.baseUrl}/change-password`, data);
  }

  // Reports
  async getRevenueReport(params?: { date_filter?: string; start_date?: string; end_date?: string; county_id?: number }): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams();
    if (params?.date_filter) queryParams.append('date_filter', params.date_filter);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.county_id) queryParams.append('county_id', String(params.county_id));
    const queryString = queryParams.toString();
    return apiClient.get<any>(`${this.baseUrl}/reports/revenue${queryString ? `?${queryString}` : ''}`);
  }

  async getTransactionReport(params?: { date_filter?: string; start_date?: string; end_date?: string; status?: string; county_id?: number }): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams();
    if (params?.date_filter) queryParams.append('date_filter', params.date_filter);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.county_id) queryParams.append('county_id', String(params.county_id));
    const queryString = queryParams.toString();
    return apiClient.get<any>(`${this.baseUrl}/reports/transactions${queryString ? `?${queryString}` : ''}`);
  }

  async getServiceUsageReport(params?: { date_filter?: string; start_date?: string; end_date?: string; county_id?: number }): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams();
    if (params?.date_filter) queryParams.append('date_filter', params.date_filter);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.county_id) queryParams.append('county_id', String(params.county_id));
    const queryString = queryParams.toString();
    return apiClient.get<any>(`${this.baseUrl}/reports/service-usage${queryString ? `?${queryString}` : ''}`);
  }

  async getAuditTrailReport(page?: number, filters?: { county_id?: number; user_id?: number; action?: string; date_from?: string; date_to?: string }): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams();
    if (page) queryParams.append('page', String(page));
    if (filters?.county_id) queryParams.append('county_id', String(filters.county_id));
    if (filters?.user_id) queryParams.append('user_id', String(filters.user_id));
    if (filters?.action) queryParams.append('action', filters.action);
    if (filters?.date_from) queryParams.append('date_from', filters.date_from);
    if (filters?.date_to) queryParams.append('date_to', filters.date_to);
    const queryString = queryParams.toString();
    return apiClient.get<any>(`${this.baseUrl}/reports/audit-trail${queryString ? `?${queryString}` : ''}`);
  }

  // County Onboarding Form Data
  async getAllCounties(): Promise<ApiResponse<any[]>> {
    return apiClient.get<any[]>(`${this.baseUrl}/form-data/counties`);
  }

  async getContacts(): Promise<ApiResponse<any[]>> {
    return apiClient.get<any[]>(`${this.baseUrl}/form-data/contacts`);
  }

  async getAllModuleTypes(): Promise<ApiResponse<any[]>> {
    return apiClient.get<any[]>(`${this.baseUrl}/form-data/module-types`);
  }
}

export const superAdminService = new SuperAdminService();
export default superAdminService;
